import { Box, Button, Flex, useDisclosure } from "@chakra-ui/react";
import { FieldTable } from "./field-table";
import { FieldViewModel } from "./field.view-model";
import { FieldModal } from "./field-modal";
import { ButtonNewItem } from "components/elements/buttons/button-new-item";
import { ReactNode, useState } from "react";
import { SectionViewModel } from "../sections/section.view-model";
import { LoanTypeViewModel } from "../loan-types/loan-type.view-model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FieldsReorder } from "./fields-reorder";
import { HideFromBorrowersSwitch } from "./hide-from-borrowers-switch";
import { checkUiConfiguration } from "utils/uiconfighelper";
import { useTenant } from "modules/tenant/providers/tenant-provider";

interface FieldsActionsProps {
  children: ReactNode;
}

export function FieldsActions({ children }: FieldsActionsProps) {
  return <>{children}</>;
}

interface FieldsProps {
  loan_type: LoanTypeViewModel | null;
  section?: SectionViewModel | null;
  fields: FieldViewModel[];
  onFieldAdded: (field: FieldViewModel) => void;
  onFieldUpdated: (field: FieldViewModel) => void;
  onFieldDeleted: (field: FieldViewModel) => void;
  onFieldsReordered: (fields: FieldViewModel[]) => void;
  onHideFromBorrowers: (fields: FieldViewModel[]) => void;
  children?: ReactNode;
}

export function Fields({
  loan_type,
  section,
  fields,
  onFieldAdded,
  onFieldUpdated,
  onFieldDeleted,
  onFieldsReordered,
  onHideFromBorrowers,
  children,
}: FieldsProps) {
  const { user } = useTenant();
  const [reordering, setReordering] = useState(false);

  // Field Modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [field, setField] = useState<FieldViewModel | null>(null);

  function handleOnClose() {
    onClose();
    setField(null);
  }

  function handleOpenModal(data?: FieldViewModel) {
    setField(data ?? createNewField(loan_type?.id, section?.id));
    onOpen();
  }
  //////////////////////

  // Re-order fields
  function handleReOrderFields(fields: FieldViewModel[]) {
    onFieldsReordered(fields);
    setReordering(false);
  }
  //////////////////////

  function isLoanSectionEnabled() {
    return checkUiConfiguration(user, "loan_section");
  }

  return (
    <>
      {reordering ? (
        <FieldsReorder
          loanType={loan_type}
          section={section}
          fields={fields}
          onSaveChanges={handleReOrderFields}
          onCancel={() => setReordering(false)}
        />
      ) : (
        <>
          <Flex direction="column" gap={4} my={4}>
            <Box>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex direction="row" gap={3} alignItems="center">
                  {fields.length > 1 && (
                    <Button
                      variant="outline"
                      leftIcon={<FontAwesomeIcon icon={["far", "bars"]} />}
                      onClick={() => setReordering(true)}
                    >
                      Re-Order Fields
                    </Button>
                  )}
                  {children}
                </Flex>
                {fields.length > 0 && isLoanSectionEnabled() && (
                  <Box>
                    <HideFromBorrowersSwitch
                      fields={fields}
                      section={section}
                      onHideFromBorrowers={onHideFromBorrowers}
                    />
                  </Box>
                )}
              </Flex>
            </Box>

            <Box>
              <FieldTable fields={fields} onOpen={field => handleOpenModal(field)} />
            </Box>
          </Flex>

          <ButtonNewItem onClick={() => handleOpenModal()}>Add New Field</ButtonNewItem>
        </>
      )}

      {field && (
        <FieldModal
          field={field}
          loanType={loan_type}
          section={section}
          isOpen={isOpen}
          onClose={handleOnClose}
          onAdd={onFieldAdded}
          onDelete={onFieldDeleted}
          onUpdate={onFieldUpdated}
        />
      )}
    </>
  );
}

function createNewField(
  loanTypeId: string | undefined,
  sectionId: string | undefined
): FieldViewModel {
  return {
    id: "",
    label: "",
    type: "text",
    placeholder: "",
    required: false,
    underwriting: false,
    options: [],
    sectionId: sectionId ?? null,
    calculated: false,
    show_on_lender_application: true,
    show_on_lender_loan: true,
    show_on_borrower_application: true,
    show_on_borrower_loan: true,
    do_not_allow_borrower_to_edit: false,
    editable: true,
    order: 0,
    data_key: "",
    identifier_key: "",
    loan_type: loanTypeId ?? null,
  };
}
